import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Plus, UserCheck, Calendar, DollarSign } from 'lucide-react';

export default function ContractsPage() {
  const { api } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [ndas, setNdas] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('contracts');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ freelancer_id: '', project_id: '', rate_type: 'per_shot', rate_amount: '', terms: '' });
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/contracts').catch(() => ({ data: [] })),
      api.get('/nda/list').catch(() => ({ data: [] })),
      api.get('/users').catch(() => ({ data: [] })),
      api.get('/projects').catch(() => ({ data: [] })),
    ]).then(([c, n, u, p]) => {
      setContracts(c.data);
      setNdas(n.data);
      setUsers(u.data);
      setProjects(p.data);
    });
  }, [api]);

  const createContract = async () => {
    await api.post('/contracts', { ...form, rate_amount: parseFloat(form.rate_amount) || 0 });
    const c = await api.get('/contracts');
    setContracts(c.data);
    setShowCreate(false);
  };

  const freelancers = users.filter(u => u.role === 'freelancer');
  const getName = (id) => users.find(u => u.id === id)?.full_name || id;

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Contracts & NDAs</h1>
          <p className="text-xs text-zinc-500 mt-1">Manage freelancer contracts and NDA lifecycle</p>
        </div>
        <button data-testid="create-contract-btn" onClick={() => setShowCreate(true)} className="h-8 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> New Contract
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-0">
        {[{k:'contracts',l:`Contracts (${contracts.length})`},{k:'ndas',l:`NDAs (${ndas.length})`}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${tab === t.k ? 'border-blue-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[28rem]">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Create Contract</h3>
            <div className="space-y-2.5">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Freelancer</label>
                <select value={form.freelancer_id} onChange={e => setForm({...form, freelancer_id: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2">
                  <option value="">Select</option>
                  {freelancers.map(f => <option key={f.id} value={f.id}>{f.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Project</label>
                <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2">
                  <option value="">All Projects</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase">Rate Type</label>
                  <select value={form.rate_type} onChange={e => setForm({...form, rate_type: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2">
                    {['per_shot', 'per_hour', 'milestone', 'fixed'].map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase">Rate (INR)</label>
                  <input value={form.rate_amount} onChange={e => setForm({...form, rate_amount: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Terms</label>
                <textarea value={form.terms} onChange={e => setForm({...form, terms: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 py-2" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createContract} className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm">Create</button>
              <button onClick={() => setShowCreate(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'contracts' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <table className="w-full" data-testid="contracts-table">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 font-medium">Freelancer</th>
                <th className="text-left px-4 py-2.5 font-medium">Rate</th>
                <th className="text-left px-4 py-2.5 font-medium">Type</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/30">
              {contracts.map(c => (
                <tr key={c.id} className="hover:bg-zinc-800/20 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-zinc-300">{getName(c.freelancer_id)}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-emerald-400">{c.rate_amount?.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-[10px] text-zinc-500">{c.rate_type?.replace('_', ' ')}</td>
                  <td className="px-4 py-2.5"><span className={`status-${c.status === 'active' ? 'completed' : 'pending'} px-1.5 py-0.5 rounded-full text-[9px]`}>{c.status}</span></td>
                  <td className="px-4 py-2.5 text-[10px] font-mono text-zinc-600">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
                </tr>
              ))}
              {contracts.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-xs text-zinc-600">No contracts yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'ndas' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 font-medium">User</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 font-medium">Signed At</th>
                <th className="text-left px-4 py-2.5 font-medium">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/30">
              {ndas.map(n => (
                <tr key={n.id} className="hover:bg-zinc-800/20 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-zinc-300">{getName(n.user_id)}</td>
                  <td className="px-4 py-2.5"><span className={`status-${n.status === 'signed' ? 'completed' : 'pending'} px-1.5 py-0.5 rounded-full text-[9px]`}>{n.status}</span></td>
                  <td className="px-4 py-2.5 text-[10px] font-mono text-zinc-600">{n.signed_at ? new Date(n.signed_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-2.5 text-[10px] font-mono text-zinc-600">{n.expires_at || 'No expiry'}</td>
                </tr>
              ))}
              {ndas.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-xs text-zinc-600">No NDAs recorded</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
